import React from 'react';
import PropTypes from 'prop-types';

import { withRouter } from 'react-router-dom';

import User from '~/models/User';

import defaultValues from '~/modules/defaultValues';

import {
    timeSign,
    compareObjectsByValueOf_OrderKey
} from '~/modules/helpers/object';
import { filterOutIntersectingObjectsByKey } from '~/modules/helpers/array';

import Translate from '~/components/Translate/Translate';

import StickyAlertContext from '~/containers/StickyAlertManager/StickyAlertContext';
import PreferencesContext from './PreferencesContext';

//----------------------------------------------------------------------------------------------------------------------

const selectedBookmarkMatcher = e => e && e.selected && e.bookmark;
const selectedQueryMatcher = e => e && e.selected;

export const sectionsWithTheNewPreferenceSystem = [
    'automation-runbook',
    'notification-queue',
    'custom-fields',
    'audit-trail',
    'audit-logged-users',
    'integrations-list',
    'entity-list',
    'triage-list',
    'incident-list',
    'incident-tasks',
    'incident-notes',
    'incident-objects',
    'incident-timeline',
    'incident-templates-settings',
    'report-template',
    'groups-settings',
    'users-settings',
    'integration-rules-settings',
    'profiles-settings',
    'playbook-executions-list'
    //'queue-settings',
    //'ark-settings'
];

export const searchParamKey = 'search';

//----------------------------------------------------------------------------------------------------------------------

// prettier-ignore
class PreferencesManager extends React.Component {
    static contextType = StickyAlertContext;

    #init() {
        this.rowKey = null;
        this.gridApi = null;
        this.filterFunction = null;
        this.lastGetPreferencesRequest = null;
        //...
        this.filterBarString = '';
    }

    #getEmptyPreferences() {
        return {
            query: [],
            visibleColumns: [], // mm: in new preferences system is replaced with 'columns' key
            sortedColumn: [],
            rowActions: [],
            rowOrder: null
        };
    }

    #getInitialState() {
        return {
            preferences: this.#getEmptyPreferences(),
            bookmarkSelection: null,
            keyMap: {},
            rows: [],
            selectedRows: [],
            filteredRows: [],
            rowsNotVisible: [],
            filterBarString: '', // 𝙦𝙪𝙚𝙧𝙮 𝘸𝘳𝘪𝘵𝘵𝘦𝘯 𝘪𝘯 𝘧𝘪𝘭𝘵𝘦𝘳𝘣𝘢𝘳, 𝘶𝘴𝘦𝘥 𝘵𝘰 𝘧𝘪𝘭𝘵𝘦𝘳 𝘵𝘩𝘦 𝘵𝘢𝘣𝘭𝘦
            hideDateTimePickerFilter: false, // Controls visibility of filter calendar.
            operatorHelpTextHiddenItems: [], // Controls visibility of operatorHelpTextHiddenItems
        };
    }

    constructor(props) {
        super(props);
        this.#init();
        this.state = this.#getInitialState();
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 𝙨𝙩𝙖𝙩𝙚 𝙢𝙤𝙙𝙞𝙛𝙞𝙚𝙧

    /**
     * Requests to update the PreferencesManager's state with the provided object's possibly updated values.
     * @param {object} obj - The object containing the values that may need to be updated.
     * @param {function} [callback]
     */
    updateState = (obj, callback) => {
        if (this.mounted && obj) {
            const entries = Object.entries(obj);
            if (entries.length) {
                // note: here we can filter locked keys
                let discarded = 0;
                let changes = 0;
                const same = {};
                const stateUpdate = {};
                const state = this.state;
                for (const [key, value] of entries) {
                    if (value !== state[key]) {
                        stateUpdate[key] = value;
                        ++changes;
                    } else {
                        same[key] = value;
                        ++discarded;
                    }
                }
                if (changes) {
                    this.setState(stateUpdate, callback);
                }
            }
        }
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 𝙪𝙨𝙚𝙧 𝙥𝙧𝙚𝙛𝙚𝙧𝙚𝙣𝙘𝙚𝙨

    /**
     * @param {string} section
     * @param {string} tag - The section tag. If not provided, section will be used as the tag
     */
    loadUserPreferences = (section, tag) => {
        const sectionTag = tag ?? section;
        const initialSectionState = {
            ...this.#getInitialState(),
            section: section,
            sectionTag: sectionTag,
        }

        if (sectionTag) {
            const { state } = this;
            if (
                section !== state.section ||
                sectionTag !== state.sectionTag // e.g.: entities.incident-objects 𝘷𝘴 entities.entity-list
            ) {
                this.updateState(
                    initialSectionState,
                    () => {
                        const sectionDefaultPrefs = defaultValues[sectionTag] || {};
                        const getPreferencesRequestStillValid = () => {
                            return this.mounted && getPreferencesRequest === this.lastGetPreferencesRequest;
                        }
                        const getPreferencesRequest =
                            this.lastGetPreferencesRequest =
                                User.getPreferences(sectionTag)
                                    .then(response => {
                                        if (getPreferencesRequestStillValid()) {
                                            const sectionPrefs = response.data?.data || {};
                                            if (!sectionPrefs.columns) {
                                                const cols = sectionPrefs.visibleColumns;
                                                if (cols?.length) {
                                                    const sorted = sectionPrefs.sortedColumn;
                                                    if (sorted?.length) {
                                                        // because is old pref system...
                                                        const { field, sort } = sorted[0]; // same as old logic, todo check logic
                                                        for (const col of cols) {
                                                            if (field === col.field) {
                                                                col.sort = sort;
                                                            }
                                                        }
                                                    } else {
                                                        for (const col of cols) {
                                                            delete col.sort; // todo check if instead delete may be assigned to null
                                                        }
                                                    }
                                                }
                                                //eb: Always use 'rowActions' from the default values
                                                const defaulRowActions = sectionDefaultPrefs.rowActions;
                                                if (defaulRowActions?.length) {
                                                    sectionPrefs.rowActions = defaulRowActions;
                                                }
                                            }
                                            
                                            // Add any default columns missing from preferences to the preferences.
                                            const defaultColumns = sectionDefaultPrefs.columns;
                                            if(defaultColumns?.length && sectionPrefs?.columns){
                                                defaultColumns.forEach(col => {
                                                    if(!sectionPrefs.columns.find(prefCol => prefCol.field === col.field)){
                                                        sectionPrefs.columns.push(col);
                                                    }
                                                })
                                            }

                                            return {
                                                ...sectionDefaultPrefs,
                                                ...sectionPrefs
                                            }
                                        }
                                    })
                                    .catch(exception => {
                                        if (APP_INFO.debug) {
                                            console.debug(exception);
                                        }
                                        return {
                                            ...sectionDefaultPrefs
                                        };
                                    })
                                    .then(newPreferences => {
                                        // 𝘧𝘪𝘯𝘢𝘭𝘭𝘺
                                        if (getPreferencesRequestStillValid()) {
                                            this.lastGetPreferencesRequest = null;
                                            timeSign(newPreferences.columns ||= []);
                                            const newBookmarkSelection = newPreferences.query?.find(selectedBookmarkMatcher)?.value ?? null;
                                            // console.log({ newPreferences, newBookmarkSelection });
                                            this.updateState({
                                                preferences: newPreferences,
                                                bookmarkSelection: newBookmarkSelection
                                            });
                                        }
                                    })
                    }
                );
            }
        } else {
            this.updateState(initialSectionState)
        }
    };

    updateFilterFunction = fn => {
        this.filterFunction = fn;
        this.setClientRowsAndApplyFilter();
    };

    evaluateFilterBarInitialValue = params => {
        if (this.mounted) {
            const queries = this.state.preferences?.query;
            const bookmarkSelection = queries?.find(
                selectedBookmarkMatcher
            );
            const querySelection = queries?.find(
                selectedQueryMatcher
            );
            const [searchParamValue] = this.getSearchParam();

            const bookmarkSelectionValue = bookmarkSelection?.value;

            const querySelectionValue = querySelection?.value;

            // console.log({ searchParamValue, bookmarkSelectionValue, querySelectionValue });

            const str = searchParamValue || bookmarkSelectionValue || querySelectionValue || '';
            const stateStr = this.state.filterBarString;

            // console.log({ str, stateStr });

            if (str !== stateStr) {
                this.setFilterBarString(str);
                if (params?.filterOnClient) {
                    this.setClientRowsAndApplyFilter();
                }
            }
        }
    };

    /**
     * @param {object} newPreferences
     * @param {object} [stateUpdate]
     * @param {function} [successCallback]
     * @param {function} [errCallback]
     */
    storeUserPreferences = (newPreferences, stateUpdate, successCallback, errCallback) => {
        const sectionTag = this.state.sectionTag;
        
        // Guard against saving preferences before section is initialized
        if (!sectionTag) {
            if (APP_INFO.debug) {
                console.warn('Attempted to save preferences before sectionTag was initialized');
            }
            // Still apply state updates and callbacks, just skip the API call
            if (stateUpdate) {
                this.setState(stateUpdate, successCallback);
            } else if (successCallback) {
                successCallback();
            }
            return;
        }
        
        User.setPreferences(sectionTag, newPreferences)
            .then(response => {
                if (200 !== response.status) {
                    throw response;
                } else {
                    // console.log('saved', sectionTag, newPreferences);
                    if (stateUpdate) {
                        // console.trace({ stateUpdate, callback });
                        this.setState(stateUpdate, successCallback);
                    }
                }
            })
            .catch(exception => {
                if (APP_INFO.debug) {
                    console.debug(exception);
                }
                if(typeof errCallback === "function"){
                    errCallback();
                }
                // stickyAlertContext
                this.context.notifyError({
                    title: <Translate message="Save preferences" />,
                    message: (
                        <Translate
                            message="An error occurred while saving {data}"
                            words={{
                                data: 'user preferences'
                            }}
                        />
                    )
                });
            });
    };

    /**
     * @typedef {Object} ColumnConfig
     * @property {string} field
     * @property {boolean} hide
     * @property {string<'left','right'>|null} pinned
     */
    /**
     * @param {Array.<ColumnConfig>} newColumns
     * @param {Function} callback
     */
    setUserColumns = (newColumns, callback) => {
        const { sectionTag, preferences: { visibleColumns, ...newPreferences } = {}} = this.state;
        if (sectionsWithTheNewPreferenceSystem.includes(sectionTag)) {
            newPreferences.columns = newColumns;
        } else {
            // 𝘭𝘦𝘨𝘢𝘤𝘺 𝘱𝘳𝘦𝘧𝘦𝘳𝘦𝘯𝘤𝘦 𝘧𝘰𝘳𝘮𝘢𝘵, 𝗐𝗂𝗅𝗅 𝖻𝖾 𝖽𝖾𝖼𝗈𝗆𝗆𝗂𝗌𝗌𝗂𝗈𝗇𝖾𝖽
            newPreferences.visibleColumns = newColumns.filter(e => e.selected);
        }
        const stateUpdate = { preferences: newPreferences };
        this.storeUserPreferences(newPreferences, stateUpdate, callback);
    };

    /**
     * @param {array} [newQueries=[]]
     */
    setUserQueries = newQueries => {
        // console.log({ newQueries });
        const selectedBookmark = newQueries?.find(selectedBookmarkMatcher);
        const newBookmarkSelection = selectedBookmark?.value ?? null;
        // console.log({ selectedBookmark, newBookmarkSelection });
        const selectedQuery = newQueries?.find(selectedQueryMatcher);
        const newQuerySelection = selectedQuery?.value ?? null;
        // console.log({ selectedQuery, newQuerySelection });
        const newFilterBarString = newBookmarkSelection || newQuerySelection || '';
        const query = timeSign(newQueries || []);
        const newPreferences = {
            ...(this.state.preferences || {}),
            query: query
        };
        const stateUpdate = {
            preferences: newPreferences,
            filterBarString: newFilterBarString,
            bookmarkSelection: newBookmarkSelection
        };
        const callback = () => {
            this.setFilterBarString(newFilterBarString);
        };
        // console.log('𝖯𝗋𝖾𝖿𝖾𝗋𝖾𝗇𝖼𝖾𝗌𝖬𝖺𝗇𝖺𝗀𝖾𝗋 𝙨𝙚𝙩𝘜𝘴𝘦𝘳𝘘𝘶𝘦𝘳𝘪𝘦𝘴', { newPreferences, stateUpdate });
        this.storeUserPreferences(newPreferences, stateUpdate, callback);
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    getSearchParam = () => {
        const { history, location } = this.props;
        const { search } = location;
        const urlSearchParams = new URLSearchParams(search);
        const searchParamValue = urlSearchParams.get(searchParamKey);
        const extras = { urlSearchParams, history, location, search };
        return [searchParamValue, extras];
    };

    setSearchParam = (value, callback) => {
        const [searchParamValue, extrasIn] = this.getSearchParam();
        const str = value || '';
        const encodedStr = str && encodeURIComponent(decodeURIComponent(str));
        if (encodedStr !== searchParamValue) {
            const { urlSearchParams, history, location } = extrasIn;
            if (encodedStr) {
                urlSearchParams.set(searchParamKey, encodedStr);
            } else {
                urlSearchParams.delete(searchParamKey);
            }
            const newLocation = `${location.pathname}?${urlSearchParams.toString()}`;
            const extrasOut = { ...extrasIn, str, encodedStr, newLocation };
            history.replace(newLocation);
            callback?.(str, extrasOut);
        }
    };

    /**
     * @param {string} value
     * @param {object} updates
     * @param {function} callback
     */
    setFilterBarString = (value, updates, callback) => {
        // console.trace('setFilterBarString', { value, updates });
        const str = this.filterBarString = value || '';
        const stateUpdate = { ...(updates || {}), filterBarString: str };
        this.setSearchParam(
            str,
            () => {
                // console.trace({ stateUpdate, callback });
                this.setState(stateUpdate, callback);
            }
        );
    };

    /**
     * @param {Object} options - The options object.
     * @param {boolean} [options.forcedQuery]
     * @param {boolean} [options.savePreferences] - Indicates if the preferences should be saved.
     */
    clearFilterBar = options => {
        const { forcedQuery, savePreferences } = options || {};
        const prevPrefs = this.state.preferences || {};
        const newQuery = prevPrefs.query?.map(
            e => {
                e.selected = false;
                return e;
            }
        ) || [];
        const newPrefs = {
            ...prevPrefs,
            query: newQuery
        };
        const stateUpdate = {
            preferences: newPrefs,
            bookmarkSelection: null
        };
        const callback =
            savePreferences
                && (() => {
                    this.storeUserPreferences(newPrefs);
                  });
        this.setSearchParam(
            forcedQuery, // 𝘧𝘢𝘭𝘴𝘺 𝘣𝘦𝘤𝘰𝘮𝘦 𝘦𝘮𝘱𝘵𝘺 forcedQueryApplied
            forcedQueryApplied => {
                this.setFilterBarString(
                    forcedQueryApplied,
                    stateUpdate,
                    callback
                );
            }
        );
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 𝙤𝙣𝙡𝙮 𝙘𝙡𝙞𝙚𝙣𝙩-𝙨𝙞𝙙𝙚

    /**
     * @param {array} [newRows]
     */
    setClientRowsAndApplyFilter = newRows => {
        const stateRows = this.state.rows;
        let update = newRows || stateRows || [];

        // Check if filter includes deleted field (deleted:)
        // This avoids matching "deleted" when it's part of a playbook name or other content
        const filterString = this.filterBarString || this.state.filterBarString || '';

        // Decode the filter string to handle encoded URLs (e.g., deleted%3Atrue -> deleted:true)
        const decodedFilterString = filterString ? decodeURIComponent(filterString) : '';
        const includesDeletedFilter = /\b(?:deleted)\b\s*[:=]/i.test(decodedFilterString);

        // Filter out deleted records unless user explicitly queries for deleted field
        // When deleted filter is present, filterFunction will handle the actual filtering logic
        if (!includesDeletedFilter) {
            update = update.filter(row => !(row?.deleted || row?.is_deleted));
        }
        
        const filteredRows = this.filterFunction?.(update) ?? update;
        if (filteredRows?.length) {
            const rowOrderPref = this.state.preferences?.rowOrder;
            if ('custom' === rowOrderPref) {
                filteredRows.sort(compareObjectsByValueOf_OrderKey);
            }
        }
        let rowsNotVisible = [];
        if (this.gridApi) {
            const rowKey = this.rowKey;
            if (rowKey) {
                rowsNotVisible = filterOutIntersectingObjectsByKey(stateRows, filteredRows, rowKey);
                const fn = (addOrRemove, update, actual) => {
                    if (update.length > actual.length) {
                        this.gridApi?.applyTransaction({
                            [addOrRemove]: filterOutIntersectingObjectsByKey(update, actual, rowKey)
                        });
                    }
                };
                fn('remove', rowsNotVisible, this.state.rowsNotVisible);
                fn('add', filteredRows, this.state.filteredRows);
                //TODO rn: avaluate if is necessary this lines to avoid row deselection
                const selectedRows = this.state.selectedRows;
                if (selectedRows?.length) {
                    for (const row of selectedRows) {
                        this.gridApi?.getRowNode(`${row[rowKey]}`)?.setSelected(true);
                    }
                }
            }
        }
        this.setState(
            {
                filteredRows: filteredRows,
                rowsNotVisible: rowsNotVisible
            },
            () => {
                this.gridApi?.applyTransaction({
                    update: update
                });
            }
        );
    };

    /**
     * @param {string} str
     */
    forceApplyFilter = str => {
        this.clearFilterBar({ forcedQuery: str });
        this.setClientRowsAndApplyFilter();
    };

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - 𝙘𝙤𝙢𝙥𝙤𝙣𝙚𝙣𝙩 𝙡𝙞𝙛𝙚-𝙘𝙮𝙘𝙡𝙚

    #manageSearchString(str) {
        const searchParams = new URLSearchParams(str);
        const searchParamValue = searchParams.get(searchParamKey);
        if (searchParamValue) {
            const decodedStr = decodeURIComponent(searchParamValue);
            if (decodedStr !== this.filterBarCurrentValueRef) {
                const newPreferences = {
                    ...(this.state.preferences || {})
                };
                const stateUpdate = {
                    preferences: newPreferences,
                    filterBarString: decodedStr,
                };
                const callback = () => {
                    this.setFilterBarString(decodedStr);
                };
                this.storeUserPreferences(newPreferences, stateUpdate, callback);
                /*this.forceQueryExecution(decodedStr);
                this.applyFilterPreferences();*/
            }
        }
    }

    componentDidMount() {
        this.mounted = true; // 𝘊𝘢𝘯𝘯𝘰𝘵 𝘴𝘦𝘵 𝘱𝘳𝘰𝘱𝘦𝘳𝘵𝘺 "isMounted" 𝘰𝘧 𝘤𝘭𝘢𝘴𝘴 𝘰𝘮𝘱𝘰𝘯𝘦𝘯𝘵 𝘸𝘩𝘪𝘤𝘩 𝘩𝘢𝘴 𝘰𝘯𝘭𝘺 𝘢 𝘨𝘦𝘵𝘵𝘦𝘳
        const { navigation } = globalThis;
        if (navigation && navigation.type === navigation.TYPE_RELOAD) {
            const locationSearch = this.props.location?.search;
            if (locationSearch) {
                this.#manageSearchString(locationSearch);
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        //!this.state.filterBarString && console.trace(this.state.filterBarString);
        /**-/
        console.log('𝖯𝗋𝖾𝖿𝖾𝗋𝖾𝗇𝖼𝖾𝗌𝖬𝖺𝗇𝖺𝗀𝖾𝗋 𝙘𝙤𝙢𝙥𝙤𝙣𝙚𝙣𝙩𝘿𝙞𝙙𝙐𝙥𝙙𝙖𝙩𝙚 %O', {
            prevProps,
            currProps: this.props,
            prevState,
            currState: this.state
        });
        /**/
        /**-/
        {
            let changedProps = 0;
            const diffProps = {};
            const currProps =  this.props;
            for (const [key,value] of Object.entries(currProps)) {
                if (value !== prevProps[key]) {
                    diffProps[key] = value;
                     ++changedProps;
                }
            }
            let changedState = 0;
            const diffState = {};
            const currState =  this.state;
            for (const [key,value] of Object.entries(currState)) {
                if (value !== prevState[key]) {
                    diffState[key] = value;
                    ++changedState;
                }
            }
            console.log({ changedProps, diffProps, changedState, diffState }, currState.preferences?.query);
        }
        /**/
        const { section, sectionTag } = this.state;
        if (
            section !== prevState.section ||
            sectionTag !== prevState.sectionTag
        ) {
            this.loadUserPreferences(section, sectionTag);
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        this.#init();
    }

    render() {
        const {
            loadUserPreferences,
            storeUserPreferences,
            setUserColumns,
            setUserQueries,
            updateState,
            setFilterBarString,
            clearFilterBar,
            forceApplyFilter,
            setClientRowsAndApplyFilter,
            updateFilterFunction,
            evaluateFilterBarInitialValue,
            state: {
                avatar,
                section,
                sectionTag,
                keyMap,
                columnCompletions,
                preferences,
                useQueries,
                useBookmarks,
                bookmarkSelection,
                rows,
                filteredRows,
                filterBarString,
                kansoIsCollapsed,
                hideDateTimePickerFilter,
                operatorHelpTextHiddenItems
            }
        } = this;
        return (
            <PreferencesContext.Provider
                value={{
                    avatar,

                    section,
                    sectionTag,

                    keyMap,
                    columnCompletions,

                    updateFilterFunction,
                    evaluateFilterBarInitialValue,

                    preferences,
                    loadUserPreferences,
                    storeUserPreferences,

                    setUserColumns,

                    useQueries,
                    setUserQueries,

                    useBookmarks,

                    bookmarkSelection,

                    updateState,

                    filterBarString,
                    setFilterBarString,
                    clearFilterBar,

                    rows,
                    filteredRows,
                    forceApplyFilter,
                    setClientRowsAndApplyFilter,

                    kansoIsCollapsed,

                    hideDateTimePickerFilter,
                    operatorHelpTextHiddenItems
                }}
            >
                {this.props.children}
            </PreferencesContext.Provider>
        );
    }
}

PreferencesManager.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.element),
        PropTypes.object
    ])
};

export default withRouter(PreferencesManager);
